import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Car, ShieldCheck, Users, TrendingUp, FileSpreadsheet, ClipboardList,
  Loader2, Eye, EyeOff, ArrowRight,
} from "lucide-react";

const DEMO = [
  { label: "Team Leader", name: "Rajesh Kumar", email: "admin@rvsf.com", password: "admin123", icon: ShieldCheck },
  { label: "Employee A", name: "Rahul Sharma", email: "rahul@rvsf.com", password: "rahul123", icon: Users },
  { label: "Employee B", name: "Amit Verma", email: "amit@rvsf.com", password: "amit123", icon: Users },
];

const FEATURES = [
  { icon: ClipboardList, text: "Capture dealership & vehicle leads in under a minute" },
  { icon: TrendingUp, text: "Track the pipeline from New to Deal Done to Completed" },
  { icon: FileSpreadsheet, text: "Monthly team reports with one-click Excel export" },
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const doLogin = async (em, pw) => {
    setLoading(true);
    try {
      const u = await login(em, pw);
      toast.success(`Welcome back, ${u.full_name.split(" ")[0]}`);
      navigate(u.role === "team_leader" ? "/team" : "/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed. Check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    doLogin(email, password);
  };

  const quick = (d) => {
    setEmail(d.email);
    setPassword(d.password);
    doLogin(d.email, d.password);
  };

  return (
    <div className="grid min-h-screen bg-background lg:grid-cols-2">
      {/* Brand panel */}
      <div className="relative hidden overflow-hidden bg-gradient-navy p-12 lg:flex lg:flex-col lg:justify-between">
        <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-brand/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-16 h-96 w-96 rounded-full bg-primary-glow/20 blur-3xl" />
        <div className="relative flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/10 text-primary-foreground ring-1 ring-white/20">
            <Car className="h-6 w-6" />
          </div>
          <div>
            <div className="font-display text-xl font-extrabold text-primary-foreground">RVSF CRM</div>
            <div className="text-xs text-primary-foreground/70">Dealership & Vehicle Sourcing</div>
          </div>
        </div>

        <div className="relative">
          <h2 className="font-display text-4xl font-extrabold leading-tight text-primary-foreground">
            Source more vehicles.<br />Close more deals.
          </h2>
          <p className="mt-3 max-w-md text-primary-foreground/70">
            One workspace for your field team to manage dealerships, leads, follow-ups and deal processing — built mobile-first.
          </p>
          <ul className="mt-8 space-y-3">
            {FEATURES.map((f) => (
              <li key={f.text} className="flex items-center gap-3 text-sm text-primary-foreground/90">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 ring-1 ring-white/15">
                  <f.icon className="h-4 w-4" />
                </span>
                {f.text}
              </li>
            ))}
          </ul>
        </div>

        <div className="relative text-xs text-primary-foreground/50">
          © {new Date().getFullYear()} RVSF CRM — Internal business tool
        </div>
      </div>

      {/* Form panel */}
      <div className="flex flex-col justify-center px-5 py-10 sm:px-10">
        <div className="mx-auto w-full max-w-sm">
          <div className="mb-8 flex items-center gap-2.5 lg:hidden">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-navy text-primary-foreground">
              <Car className="h-5 w-5" />
            </div>
            <div>
              <div className="font-display text-lg font-extrabold">RVSF CRM</div>
              <div className="text-[11px] text-muted-foreground">Vehicle Sourcing</div>
            </div>
          </div>

          <h1 className="text-2xl font-bold text-foreground">Sign in</h1>
          <p className="mt-1 text-sm text-muted-foreground">Access your leads, dealerships and reports.</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" autoComplete="username" className="h-11"
                placeholder="you@rvsf.com" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input id="password" type={show ? "text" : "password"} autoComplete="current-password"
                  className="h-11 pr-10" placeholder="••••••••" value={password}
                  onChange={(e) => setPassword(e.target.value)} required />
                <button type="button" onClick={() => setShow((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label="Toggle password">
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" variant="brand" size="lg" className="w-full" disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
              Sign in
            </Button>
          </form>

          <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground">
            <span className="h-px flex-1 bg-border" /> Quick demo login <span className="h-px flex-1 bg-border" />
          </div>

          <div className="space-y-2">
            {DEMO.map((d) => (
              <button key={d.email} onClick={() => quick(d)} disabled={loading}
                className="flex w-full items-center gap-3 rounded-xl border border-border bg-card p-3 text-left shadow-card transition-shadow hover:shadow-elevated disabled:opacity-60">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent text-accent-foreground">
                  <d.icon className="h-4 w-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-foreground">{d.label} · {d.name}</span>
                  <span className="block truncate text-xs text-muted-foreground">{d.email}</span>
                </span>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
